import { useState, type FormEvent } from "react";
import { Mail } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api/public.functions";
import { cn } from "@/lib/utils";

type NewsletterSubscribeFormProps = {
  source?: string;
  className?: string;
  variant?: "dark" | "light";
  showHeading?: boolean;
};

export function NewsletterSubscribeForm({
  source = "insights_newsletter",
  className,
  variant = "dark",
  showHeading = true,
}: NewsletterSubscribeFormProps) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const isDark = variant === "dark";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await subscribeNewsletter({ data: { email: trimmed, source } });
      if (result.reactivated) {
        setFeedback({ type: "success", message: "Welcome back — you're subscribed again." });
      } else if (result.alreadySubscribed) {
        setFeedback({ type: "success", message: "You're already on our list. Thanks!" });
      } else {
        setFeedback({ type: "success", message: "Thanks for subscribing — we'll be in touch." });
      }
      setEmail("");
    } catch (err) {
      setFeedback({
        type: "error",
        message: err instanceof Error ? err.message : "Could not subscribe. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={className}>
      {showHeading ? (
        <div className="flex items-center gap-3">
          <Mail className={cn("h-6 w-6", isDark ? "text-gold" : "text-primary")} strokeWidth={1.5} />
          <h3 className={cn("text-2xl", isDark ? "text-primary-foreground" : "text-primary")}>Stay Informed</h3>
        </div>
      ) : null}
      <p
        className={cn(
          "max-w-sm text-base leading-relaxed",
          showHeading ? "mt-4" : "",
          isDark ? "text-white/85" : "text-muted-foreground",
        )}
      >
        Subscribe to our newsletter and receive the latest insights and updates that matter to your business.
      </p>
      <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          required
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (feedback) setFeedback(null);
          }}
          placeholder="Enter your email address"
          disabled={submitting}
          className={cn(
            "flex-1 rounded-md border px-4 py-3 text-base focus:outline-none disabled:opacity-60",
            isDark
              ? "border-white/30 bg-white/10 text-white placeholder:text-white/60 focus:border-white focus:bg-white/15"
              : "border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary",
          )}
        />
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-gold px-6 py-3 text-base font-semibold text-primary-foreground transition hover:bg-gold/90 disabled:opacity-60"
        >
          {submitting ? "Subscribing…" : "Subscribe"}
        </button>
      </form>
      {feedback ? (
        <p
          role="status"
          aria-live="polite"
          className={cn(
            "mt-3 text-sm font-medium",
            feedback.type === "success"
              ? isDark
                ? "text-emerald-200"
                : "text-emerald-700"
              : isDark
                ? "text-red-200"
                : "text-red-600",
          )}
        >
          {feedback.message}
        </p>
      ) : (
        <p className={cn("mt-3 text-sm", isDark ? "text-white/60" : "text-muted-foreground")}>
          We respect your privacy. Unsubscribe at any time.
        </p>
      )}
    </div>
  );
}
